import { useContext, useState, useEffect, useRef } from "react";
import BackdropPortal from "../../components/shared/BackdropPortal";
import { snackbarService } from "@one/web-components/dist/index.cjs.js";
import { useFormikContext } from "formik";
import { connect, useSelector } from "react-redux";
import { selectIsPredOrGredUser } from "../../selectors/user";
import { withApollo, compose } from "react-apollo";
import differenceBy from "lodash/differenceBy";
import _ from "underscore";
import { v4 as uuid } from "uuid";
import {
  CREATE_BOOKING_COMMAND,
  UPDATE_BOOKING,
  CREATE_INFORMATION_BOOKING,
  UPDATE_INFORMATION_BOOKING,
  BATCH_CREATE_INFORMATION_BOOKINGS,
  DROP_BOOKING,
} from "../../gql/bookingapi/mutations";
import { INFORMATION_BOOKING_IDS_BY_BATCH_ID } from "../../gql/bookingapi/queries";
import {
  DURATION,
  UPDATE_REQUEST,
  CANCEL_REQUEST,
  BOOKING_REQUEST,
  BOOKING_ADMIN,
  FORCE_TIMELINE_RANGE_KEY,
  GROUP_VIEW_PREFILL_STORAGE_KEY,
  INFORMATION_BOOKING_TYPE,
  INFORMATION_BOOKING_COLOR,
  INFORMATION_BOOKING_FCOLOR,
} from "../../constants";
import { CustomFilterContext } from "../booking-instruments/booking-instruments-custom-filter/context";
import BookingNotificationStatus from "../booking-calendars/booking-notification-status/BookingNotificationStatus";
import BookingScheduleStatus from "./calendar-timeline/BookingScheduleStatus";
import { timeoutCallbackFactory } from "../booking-calendars/booking-notification-status/timeoutCallbackFactory";
import { loadPendingReservations as loadPendingReservationsAction } from "../booking-calendars/redux/actions";
import { useHistory } from "react-router-dom";
import { BookingContext } from "../booking/context";
import ConfirmDialog from "../../components/shared/ConfirmDialog";
import moment from "moment";
import omitDeep from "omit-deep-lodash";
import { hasAllRequiredRoles } from "../../utils/hooks/withRoles";
import { detectOverlapWithinPending } from "./calendar-timeline/overlapDetection.utils";
import BookingActionButtons from "./BookingActionButtons";
import RecurringBookingContext from "./calendar-timeline/RecurringBooking/Context/RecurringBookingContext";

const transformElnId = (elnId) => {
  if (!elnId) return null;
  if (!elnId.eid) {
    return { name: elnId.name };
  }
  return elnId;
};

const clearGroupViewPrefillStorage = () => {
  try {
    sessionStorage.removeItem(GROUP_VIEW_PREFILL_STORAGE_KEY);
  } catch (_e) {
    // Non-blocking cleanup.
  }
};

export const getReservationInventoryId = (reservation) => reservation?.resource || reservation?.inventoryId;

export const getCreateAssistantVariables = (values, user) => {
  const isPredOrGredUser = selectIsPredOrGredUser(user);
  if (!isPredOrGredUser || !values?.assistant) return {};

  return {
    assistent: [values.assistant],
    assistentTime: values.assistentTime ?? null,
  };
};

export const buildUpdateEntries = ({ bookingEntries = [], pendingReservations = [], reasonValue }) => {
  const tempEventArray = [];

  bookingEntries.forEach((item) => {
    const rowFound = pendingReservations.find((x) => x.id === item.id);
    if (rowFound === undefined) {
      tempEventArray.push({
        allDay: false,
        dateFrom: item?.start,
        dateTo: item?.end,
        bookingEntryId: item?.id,
        remark: reasonValue,
        inventoryId: item?.inventoryId,
        commandType: CANCEL_REQUEST,
      });
    }
  });

  pendingReservations.forEach((item) => {
    const rowFound = bookingEntries.find((x) => x.id === item.id);
    if (rowFound) {
      tempEventArray.push({
        allDay: false,
        dateFrom: item?.start,
        dateTo: item?.end,
        inventoryId: getReservationInventoryId(item),
        bookingEntryId: item?.id,
        remark: reasonValue,
        commandType: UPDATE_REQUEST,
      });
      return;
    }

    tempEventArray.push({
      allDay: false,
      dateFrom: item?.start,
      dateTo: item?.end,
      inventoryId: getReservationInventoryId(item),
      commandType: BOOKING_REQUEST,
    });
  });

  return tempEventArray;
};

export const hasReservationChangesForSelectedInventory = ({
  pendingReservations = [],
  bookingEntries = [],
  selectedInventoryIds = [],
}) => {
  const shouldCheckAllReservations = selectedInventoryIds.length === 0;

  const normalizeReservation = (reservation) => ({
    id: reservation?.id,
    start: reservation?.start,
    end: reservation?.end,
    inventoryId: getReservationInventoryId(reservation),
  });

  const isInSelectedInventory = (reservation) =>
    shouldCheckAllReservations || selectedInventoryIds.includes(reservation.inventoryId);

  const sortByIdStartEnd = (a, b) => {
    const aKey = `${a.id || ""}-${a.start || ""}-${a.end || ""}-${a.inventoryId || ""}`;
    const bKey = `${b.id || ""}-${b.start || ""}-${b.end || ""}-${b.inventoryId || ""}`;
    return aKey.localeCompare(bKey);
  };

  const currentReservations = pendingReservations.map(normalizeReservation).filter(isInSelectedInventory).sort(sortByIdStartEnd);

  const initialReservations = bookingEntries.map(normalizeReservation).filter(isInSelectedInventory).sort(sortByIdStartEnd);

  return !_.isEqual(currentReservations, initialReservations);
};

export const compareBookingValues = ({ formik, pendingReservations = [], bookingEntries = [], selectedInventoryIds = [] }) => {
  const valueOne = omitDeep(formik?.values, "bookingFor");
  const valueTwo = omitDeep(formik?.initialValues, "bookingFor");
  const formikDiff = differenceBy(formik?.values?.bookingFor, formik?.initialValues?.bookingFor, "userId");
  const formikDiffres = differenceBy(formik?.initialValues?.bookingFor, formik?.values?.bookingFor, "userId");

  const hasReservationChanges = hasReservationChangesForSelectedInventory({
    pendingReservations,
    bookingEntries,
    selectedInventoryIds,
  });

  const checkedEquip = bookingEntries.filter((entry) =>
    selectedInventoryIds.length === 1
      ? String(entry?.inventoryId || "").indexOf(String(selectedInventoryIds[0])) === -1
      : !selectedInventoryIds.includes(entry?.inventoryId)
  );

  const hasEqualBaseValues = _.isEqual(valueOne, valueTwo);
  const hasNoBookingForDiff = !formikDiff.length && !formikDiffres.length;
  const hasNoReservationDiff = !hasReservationChanges;
  const hasNoUncheckedEquipment = !checkedEquip.length;

  return [hasEqualBaseValues, hasNoBookingForDiff, hasNoReservationDiff, hasNoUncheckedEquipment].every(Boolean);
};

export const getAssistanceSyncUpdates = ({ instruments = [], values = {}, initialValues = {} }) => {
  const requiresAssistance = instruments.some(
    (eq) => !!+eq?.shouldAssisted || !!+eq?.assistedBooking
  );

  const updates = {};

  if (requiresAssistance && values.withAssistance === undefined) {
    updates.withAssistance = false;
    if (values.assistanceReason === undefined) {
      updates.assistanceReason = "";
    }
  } else if (
    !requiresAssistance &&
    values.withAssistance !== undefined &&
    initialValues.withAssistance === undefined
  ) {
    updates.withAssistance = undefined;
    updates.assistanceReason = undefined;
  }

  return updates;
};

export const getHazardousSyncUpdates = ({ instruments = [], values = {}, initialValues = {} }) => {
  const requiresHazardous = instruments.some((eq) => !!+eq?.isHazardous);

  const updates = {};

  if (requiresHazardous && !values.hasHazardousMaterials) {
    updates.hasHazardousMaterials = true;
    updates.hazardousMaterialsText = values.hazardousMaterialsText ?? "";
  } else if (
    !requiresHazardous &&
    values.hasHazardousMaterials === true &&
    initialValues.hasHazardousMaterials !== true
  ) {
    updates.hasHazardousMaterials = initialValues.hasHazardousMaterials;
    updates.hazardousMaterialsText = initialValues.hazardousMaterialsText ?? "";
  }

  return updates;
};

export const getDeclarationSyncUpdates = ({ instruments = [], values = {}, initialValues = {} }) => ({
  ...getAssistanceSyncUpdates({ instruments, values, initialValues }),
  ...getHazardousSyncUpdates({ instruments, values, initialValues }),
});

const BookEquipmentAction = ({
  client,
  disabledSelection,
  setSubscriptionStatus,
  selectedStartDate,
  selectedEndDate,
  setPopupEnableStatus,
  activeTab,
  handlersRef,
}) => {
  const formik = useFormikContext();
  const { selectedList } = useContext(CustomFilterContext);
  const { pendingReservations } = useSelector((state) => state.reservations);
  const forceInformationOnly = useSelector((state) => state.reservations.forceInformationOnly);
  const storeUser = useSelector((store) => store.user);
  const bookingInstruments = useSelector((store) => store.bookingInstruments);
  const [fetching, setFetching] = useState(false);
  const { conflicts, getTrimmedRRuleString } = useContext(RecurringBookingContext);
  let pendingReservationsArray = [...pendingReservations];

  const {
    isEditMode,
    editBookingData,
    isDuplicate,
    isRedirectToAdmin,
    forceMyBookingsRedirect,
    keepLastUrlRedirect,
    dispatchAction: bookingDispatchAction,
  } = useContext(BookingContext);

  const [reasonValue, setReasonValue] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isLegendVisible, setIsLegendVisible] = useState(false);
  const [isConversionConfirmOpen, setIsConversionConfirmOpen] = useState(false);
  // Conversion (regular booking -> Information slot) is a two-step flow:
  // (1) async cancelBookingCommand, (2) createInformationBooking once the
  // BE subscription confirms ALL_SUCCEEDED. The ref holds the post-cancel
  // step so the shared BookingNotificationStatus subscription handler can
  // dispatch it and skip the default redirect. ISO 7367645.
  const conversionCallbackRef = useRef(null);
  const navigation = useHistory();

  useEffect(() => {
    if (!isEditMode) return;

    const updates = getDeclarationSyncUpdates({
      instruments: bookingInstruments?.bookingInstruments || [],
      values: formik.values,
      initialValues: formik.initialValues,
    });

    // A group-view (Radian) prefill link deliberately initializes the
    // hazardous declaration as non-dangerous when no material was supplied;
    // don't let the equipment-required sync immediately override that choice.
    const isGroupViewPrefillBooking = editBookingData?.prefillSource === "group-view-url";
    if (isGroupViewPrefillBooking && formik.values.hasHazardousMaterials === false) {
      delete updates.hasHazardousMaterials;
      delete updates.hazardousMaterialsText;
    }

    if (Object.keys(updates).length > 0) {
      formik.setValues({ ...formik.values, ...updates });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingInstruments.bookingInstruments, isEditMode]);

  const onReasonChanged = (reason) => {
    setReasonValue(reason);
  };

  const handleOnclose = () => {
    setIsDialogOpen(false);
    setReasonValue("");
    return true;
  };
  const handlerOnCancelDeleteBooking = () => {
    setIsDialogOpen(false);
    setReasonValue("");
  };

  const handleSend = async () => {
    setFetching(true);

    const isBookingAdmin = storeUser?.roles?.some((role) => role === "BookingAdmin");
    // BE rejects duplicate Run/Action bookings on the same timeframe, so if any
    // two pending slots overlap each other on the same resource we MUST flip to
    // Information at submit time -- even if the Redux flag wasn't set during
    // step 2 (e.g. because CalendarMainPage unmounted before its overlap effect
    // could dispatch). This is the last line of defense before the mutation.
    const pendingsOverlapEachOther = detectOverlapWithinPending(pendingReservationsArray);
    const effectiveBookingType =
      (forceInformationOnly || pendingsOverlapEachOther) && isBookingAdmin && !isEditMode && !isDuplicate
        ? INFORMATION_BOOKING_TYPE
        : formik.values.bookingType;

    if (effectiveBookingType === INFORMATION_BOOKING_TYPE) {
      try {
        const entries = pendingReservationsArray.filter(
          (r) => r?.start && r?.end && (r?.resource || r?.inventoryId)
        );

        // BE batchCreate is backed by DynamoDB BatchPutItem, hard capped at 25
        // items per call (ISO 7367645). For larger wizard passes we chunk the
        // slots and reuse the same FE-generated batchId across calls so every
        // chunk lands in the InformationBookingByBatchId GSI under one batch.
        const BATCH_CREATE_CHUNK_SIZE = 25;

        const reservedForUsers = (formik.values.bookingFor || [])
          .map((k) => k?.userId)
          .filter(Boolean);
        const redPandaProjects = (formik.values.redPandaProjects || []).map((p) => ({
          projectId: p.projectId,
          projectName: p.projectName,
          projectShortName: p.projectShortName,
          themeNumber: p.themeNumber,
        }));
        const projectFallback =
          (typeof formik.values.project === "string" && formik.values.project.trim()) || null;

        const buildSlotInput = (entry) => ({
          description: formik.values.description,
          richDescription: formik.values.richDescription || null,
          reservedForUsers,
          project: projectFallback,
          redPandaProjects,
          dateFrom: moment(entry.start).utc().format(),
          dateTo: moment(entry.end).utc().format(),
          site: storeUser.site,
          inventoryId: entry.resource || entry.inventoryId,
          color: INFORMATION_BOOKING_COLOR,
          fcolor: INFORMATION_BOOKING_FCOLOR,
        });

        let createdCount = 0;
        let failedCount = 0;
        if (entries.length > 1) {
          // Multi-slot wizard pass -> BatchPutItem on BE, non-atomic. batchId
          // is FE-generated so every chunk shares it and cascade cancel/edit
          // can target the whole batch via the InformationBookingByBatchId GSI.
          const batchId = uuid();
          for (let i = 0; i < entries.length; i += BATCH_CREATE_CHUNK_SIZE) {
            const chunk = entries.slice(i, i + BATCH_CREATE_CHUNK_SIZE);
            const input = chunk.map(buildSlotInput);
            try {
              const result = await client.mutate({
                mutation: BATCH_CREATE_INFORMATION_BOOKINGS,
                fetchPolicy: "no-cache",
                variables: { batchId, input },
              });
              const returned = result?.data?.batchCreateInformationBookings || [];
              // BatchPutItem returns nullable array elements for unprocessed
              // items, so a short result length signals a partial failure on
              // the chunk; count what actually landed.
              const successInChunk = returned.filter(Boolean).length;
              createdCount += successInChunk;
              failedCount += input.length - successInChunk;
            } catch (_e) {
              failedCount += input.length;
            }
          }
        } else if (entries.length === 1) {
          // Single slot -> classic single-row UNIT resolver, no batchId.
          await client.mutate({
            mutation: CREATE_INFORMATION_BOOKING,
            fetchPolicy: "no-cache",
            variables: { input: buildSlotInput(entries[0]) },
          });
          createdCount = 1;
        }
        if (failedCount > 0 && createdCount === 0) {
          // Whole batch failed -> propagate to outer catch for the error UX.
          throw new Error("Failed to create Information slots");
        }
        const partialSuffix = failedCount > 0 ? ` (${failedCount} failed)` : "";
        snackbarService.show({
          message: createdCount > 1
            ? `Created ${createdCount} Information slots${partialSuffix}`
            : `Information slot created${partialSuffix}`,
          type: failedCount > 0 ? "warning" : "success",
          duration: DURATION,
        });
        clearGroupViewPrefillStorage();
        formik.setValues({
          ...formik.values,
          description: "",
          project: "",
        });
        try {
          sessionStorage.setItem(
            FORCE_TIMELINE_RANGE_KEY,
            JSON.stringify({
              dateFrom: moment(selectedStartDate).utc().format(),
              dateTo: moment(selectedEndDate).utc().format(),
              createdAt: new Date().toISOString(),
            })
          );
        } catch (_e) { /* sessionStorage may be unavailable - non-critical */ }
        try {
          const LAST_URL_KEY = "app:lastUrl";
          const lastUrl = sessionStorage.getItem(LAST_URL_KEY) || "/bookings";
          let redirectPath = "/bookings";
          if (isRedirectToAdmin) {
            redirectPath = "/admin";
          } else if (keepLastUrlRedirect && !forceMyBookingsRedirect) {
            redirectPath = lastUrl;
          }
          navigation.push(redirectPath);
        } catch (_e) { /* navigation failure is non-critical */ }
      } catch (error) {
        snackbarService.show({
          message: `Failed to create information slot`,
          type: "error",
          duration: DURATION,
        });
      } finally {
        setFetching(false);
      }
      return;
    }

    let pendingReservationsList = [
      ...pendingReservationsArray.map(({ active, color, type, fcolor, id, overlap, ...rest }) => {
        return rest;
      }),
    ];
    pendingReservationsList.forEach((object) => {

      object.dateFrom = object["start"];
      object.dateTo = object["end"];
      object.inventoryId = object["resource"];
      if (!isDuplicate && object["bookingId"]) {
        object.bookingEntryId = object["bookingId"];
      } else {
        delete object.bookingEntryId;
      }
    });
    let pendingReservationsID = [
      ...pendingReservationsList.map(({ start, end, resource, bookingId, title, allDay, ...rest }) => {
        return rest;
      }),
    ];

    const variables = {
      createdBy: storeUser.user,
      createdByEmail: storeUser.email,
      createdByName: storeUser.name,
      entries: pendingReservationsID,
      type: effectiveBookingType,
      description: formik.values.description,
      richDescription: formik.values.richDescription || null,
      project: formik.values.project,
      redPandaProjects: formik.values.redPandaProjects,
      dateFrom: moment(selectedStartDate).utc().format(),
      dateTo: moment(selectedEndDate).utc().format(),
      site: storeUser.site,
      reservedForEmails: formik.values.bookingFor.map((k) => k.email),
      reservedForUsers: formik.values.bookingFor.map((k) => k.userId),
      ...(formik.values.hasHazardousMaterials !== undefined ? {
        hazardousDeclaration: {
          hasHazardousMaterials: formik.values.hasHazardousMaterials,
          hazardousMaterialsList: formik.values.hazardousMaterialsText || "",
        },
      } : {}),
      ...(formik.values.withAssistance !== undefined ? {
        assistanceDeclaration: {
          requiresAssistance: formik.values.withAssistance,
          assistanceReason: formik.values.assistanceReason || "",
        }
      } : {}),
      ...getCreateAssistantVariables(formik.values, storeUser),
      originId: formik.values.originId || null,
      elnId: transformElnId(formik.values.elnId),
      force: false,
      rrule: getTrimmedRRuleString()
    };
    variables.entries = variables.entries.filter(entry => {
      if (isDuplicate) {
        return entry?.dateTo && entry?.dateFrom && !entry?.reservedForEmails
      }
      return entry?.dateTo && entry?.dateFrom
    })
    try {
      const result = await client.mutate({
        mutation: CREATE_BOOKING_COMMAND,
        fetchPolicy: "no-cache",
        variables,
      });
      if (result.data?.createBookingCommand !== null) {
        clearGroupViewPrefillStorage();
        formik.setValues({
          ...formik.values,
          description: "",
          richDescription: "",
          project: "",
        });
      }
    } catch (error) {
      snackbarService.show({
        message: `Booking created failed!`,
        type: "error",
        duration: DURATION,
      });
    }

    try {
      sessionStorage.setItem(
        FORCE_TIMELINE_RANGE_KEY,
        JSON.stringify({
          dateFrom: variables?.dateFrom,
          dateTo: variables?.dateTo,
          createdAt: new Date().toISOString(),
        })
      );
    } catch (e) {
    }
  };
  const handleUpdate = async () => {
    // Conversion path: regular booking -> Information slot. Requires explicit
    // admin confirmation because the original booking will be permanently
    // cancelled before the Information slot is created. Gate here BEFORE
    // setFetching(true) so the dialog renders on top of the wizard.
    const wasRegularBooking =
      editBookingData?.type && editBookingData.type !== INFORMATION_BOOKING_TYPE;
    const willBeInformation = formik.values.bookingType === INFORMATION_BOOKING_TYPE;
    if (isEditMode && wasRegularBooking && willBeInformation) {
      setIsConversionConfirmOpen(true);
      return;
    }

    setFetching(true);

    if (formik.values.bookingType === INFORMATION_BOOKING_TYPE) {
      try {
        const informationId =
          editBookingData?.informationBookingId ||
          editBookingData?.id ||
          editBookingData?.bookingId;
        const reservedForUsers = (formik.values.bookingFor || [])
          .map((k) => k?.userId)
          .filter(Boolean);
        const projectFallback =
          (typeof formik.values.project === "string" && formik.values.project.trim()) || null;

        // Information slot is editable only on the documented whitelist
        // (ISO 7367645): description, project, reservedForUsers.
        // BE exposes a dedicated `updateInformationBooking` mutation whose
        // `UpdateInformationBookingInput` accepts just the patch -- the
        // immutable fields (site, inventoryId, color, fcolor, batchId) are
        // not part of the input so the GraphQL coercer rejects them
        // before they hit the resolver.
        const patch = {
          description: formik.values.description,
          richDescription: formik.values.richDescription || null,
          project: projectFallback,
          reservedForUsers,
        };

        const batchId = editBookingData?.batchId;
        let cascadedIdsCount = 0;
        let cascadedTotal = 0;
        let cascadedFailed = 0;
        if (batchId) {
          // Cascade edit: resolve sibling ids via the batch GSI, then apply
          // the patch to every slot belonging to the batch. BE has no atomic
          // batchUpdate — FE fans out single-slot CREATE upserts in parallel
          // and surfaces partial failures in the success snackbar.
          const idsResult = await client.query({
            query: INFORMATION_BOOKING_IDS_BY_BATCH_ID,
            fetchPolicy: "no-cache",
            variables: { batchId },
          });
          const ids = idsResult?.data?.informationBookingIdsByBatchId || [];
          if (ids.length === 0) {
            // Batch already gone (concurrent cancel) - nothing to update.
            snackbarService.show({
              message: "Batch is no longer available",
              type: "error",
              duration: DURATION,
            });
            setFetching(false);
            return;
          }
          // BE has no UpdateInformationBookingInput yet; the CREATE
          // upsert schema requires the immutable non-null fields. Fetch
          // each sibling first to echo its own dateFrom/dateTo/site/
          // inventoryId/color/fcolor back unchanged.
          const results = await Promise.allSettled(
            ids.map((id) =>
              client.mutate({
                mutation: UPDATE_INFORMATION_BOOKING,
                fetchPolicy: "no-cache",
                variables: { input: { id, ...patch } },
              })
            )
          );
          cascadedIdsCount = results.filter((r) => r.status === "fulfilled").length;
          cascadedTotal = results.length;
          cascadedFailed = cascadedTotal - cascadedIdsCount;
          if (cascadedIdsCount === 0) {
            throw new Error("Failed to update Information batch");
          }
        } else {
          await client.mutate({
            mutation: UPDATE_INFORMATION_BOOKING,
            fetchPolicy: "no-cache",
            variables: {
              input: {
                id: informationId,
                ...patch,
              },
            },
          });
        }
        bookingDispatchAction({ type: "resetBookingData" });
        let updateMessage;
        if (cascadedFailed > 0) {
          updateMessage = `Updated ${cascadedIdsCount} of ${cascadedTotal} Information slots (${cascadedFailed} failed)`;
        } else if (cascadedIdsCount > 1) {
          updateMessage = `Updated ${cascadedIdsCount} Information slots`;
        } else {
          updateMessage = `Information slot updated`;
        }
        snackbarService.show({
          message: updateMessage,
          type: cascadedFailed > 0 ? "warning" : "success",
          duration: DURATION,
        });
        try {
          sessionStorage.setItem(
            FORCE_TIMELINE_RANGE_KEY,
            JSON.stringify({
              dateFrom: moment(selectedStartDate).utc().format(),
              dateTo: moment(selectedEndDate).utc().format(),
              createdAt: new Date().toISOString(),
            })
          );
        } catch (_e) { /* sessionStorage may be unavailable - non-critical */ }
        try {
          const LAST_URL_KEY = "app:lastUrl";
          const lastUrl = sessionStorage.getItem(LAST_URL_KEY) || "/bookings";
          let redirectPath = "/bookings";
          if (isRedirectToAdmin) {
            redirectPath = "/admin";
          } else if (keepLastUrlRedirect && !forceMyBookingsRedirect) {
            redirectPath = lastUrl;
          }
          navigation.push(redirectPath);
        } catch (_e) { /* navigation failure is non-critical */ }
      } catch (error) {
        snackbarService.show({
          message: `Failed to update information slot`,
          type: "error",
          duration: DURATION,
        });
      } finally {
        setFetching(false);
      }
      return;
    }

    const tempEventArray = buildUpdateEntries({
      bookingEntries: editBookingData?.bookingEntries || [],
      pendingReservations,
      reasonValue,
    });

    const variables = {
      commandType: UPDATE_REQUEST,
      createdBy: storeUser.user,
      createdByEmail: storeUser.email,
      createdByName: storeUser.name,
      entries: tempEventArray,
      type: formik.values.bookingType,
      description: formik.values.description,
      richDescription: formik.values.richDescription || null,
      project: formik.values.project,
      redPandaProjects: formik.values.redPandaProjects,
      remark: reasonValue,
      bookingId: editBookingData?.bookingId || editBookingData?.id,
      dateFrom: moment(selectedStartDate).utc().format(),
      dateTo: moment(selectedEndDate).utc().format(),
      site: storeUser.site,
      reservedForEmails: formik.values.bookingFor.map((k) => k.email),
      reservedForUsers: formik.values.bookingFor.map((k) => k.userId),
      hazardousDeclaration: {
        hasHazardousMaterials: formik.values.hasHazardousMaterials ?? false,
        hazardousMaterialsList: formik.values.hazardousMaterialsText || "",
      },
      assistanceDeclaration: {
        requiresAssistance: formik.values.withAssistance ?? false,
        assistanceReason: formik.values.assistanceReason || "",
      },
      ...(formik.values.assistant ? {
        assistent: [formik.values.assistant],
        assistentTime: formik.values.assistentTime ?? null,
      } : {}),
      originId: formik.values.originId || null,
      elnId: transformElnId(formik.values.elnId),
      force: hasAllRequiredRoles(storeUser.roles, [BOOKING_ADMIN]),
    };
    try {
      await client.mutate({
        mutation: UPDATE_BOOKING,
        fetchPolicy: "no-cache",
        variables,
      });
      bookingDispatchAction({
        type: "resetBookingData",
      });

      sessionStorage.setItem(
        FORCE_TIMELINE_RANGE_KEY,
        JSON.stringify({
          dateFrom: variables?.dateFrom,
          dateTo: variables?.dateTo,
          createdAt: new Date().toISOString(),
        })
      );
    } catch (error) {
      snackbarService.show({
        message: `Booking created failed!`,
        type: "error",
        duration: DURATION,
      });
    }
  };

  // Step 2 of the conversion flow. Runs once the cancelBookingCommand
  // subscription reports ALL_SUCCEEDED. Builds Information slot inputs from
  // the same bookingEntries the user was editing and submits them via the
  // existing single/batch create mutations (the batch path mirrors the
  // create wizard in handleSend, including the 25-item DDB chunk cap).
  const runInformationCreateAfterCancel = async () => {
    try {
      const entries = (editBookingData?.bookingEntries || []).filter(
        (e) => e?.start && e?.end && (e?.inventoryId || e?.resource)
      );
      if (entries.length === 0) {
        throw new Error("No booking entries available for conversion");
      }

      const reservedForUsers = (formik.values.bookingFor || [])
        .map((k) => k?.userId)
        .filter(Boolean);
      const redPandaProjects = (formik.values.redPandaProjects || []).map((p) => ({
        projectId: p.projectId,
        projectName: p.projectName,
        projectShortName: p.projectShortName,
        themeNumber: p.themeNumber,
      }));
      const projectFallback =
        (typeof formik.values.project === "string" && formik.values.project.trim()) || null;

      // BE schema declares CreateInformationBookingInput.description as
      // non-null (String!). The original booking may have had an empty/null
      // description, so fall back through formik -> editBookingData -> a
      // single space to satisfy the GraphQL coercer (the original cancel
      // already succeeded at this point — failing on a null here would
      // leave the user without either booking or Information slot).
      const descriptionFallback =
        (typeof formik.values.description === "string" && formik.values.description.trim()) ||
        (typeof editBookingData?.description === "string" && editBookingData.description.trim()) ||
        " ";

      const buildSlotInput = (entry) => ({
        description: descriptionFallback,
        reservedForUsers,
        project: projectFallback,
        redPandaProjects,
        dateFrom: moment(entry.start).utc().format(),
        dateTo: moment(entry.end).utc().format(),
        site: storeUser.site,
        inventoryId: entry.inventoryId || entry.resource,
        color: INFORMATION_BOOKING_COLOR,
        fcolor: INFORMATION_BOOKING_FCOLOR,
      });

      const BATCH_CREATE_CHUNK_SIZE = 25;
      let createdCount = 0;
      let failedCount = 0;

      if (entries.length > 1) {
        const batchId = uuid();
        for (let i = 0; i < entries.length; i += BATCH_CREATE_CHUNK_SIZE) {
          const chunk = entries.slice(i, i + BATCH_CREATE_CHUNK_SIZE);
          const input = chunk.map(buildSlotInput);
          try {
            const result = await client.mutate({
              mutation: BATCH_CREATE_INFORMATION_BOOKINGS,
              fetchPolicy: "no-cache",
              variables: { batchId, input },
            });
            const returned = result?.data?.batchCreateInformationBookings || [];
            const successInChunk = returned.filter(Boolean).length;
            createdCount += successInChunk;
            failedCount += input.length - successInChunk;
          } catch (chunkError) {
            console.error(
              "BookEquipmentAction: batchCreateInformationBookings chunk failed during conversion",
              chunkError
            );
            failedCount += input.length;
          }
        }
      } else {
        await client.mutate({
          mutation: CREATE_INFORMATION_BOOKING,
          fetchPolicy: "no-cache",
          variables: { input: buildSlotInput(entries[0]) },
        });
        createdCount = 1;
      }

      if (createdCount === 0) {
        throw new Error("Failed to create any Information slot after cancel");
      }

      const partialSuffix = failedCount > 0 ? ` (${failedCount} failed)` : "";
      snackbarService.show({
        message:
          createdCount > 1
            ? `Booking converted to ${createdCount} Information slots${partialSuffix}`
            : `Booking converted to Information slot${partialSuffix}`,
        type: failedCount > 0 ? "warning" : "success",
        duration: DURATION,
      });

      clearGroupViewPrefillStorage();
      bookingDispatchAction({ type: "resetBookingData" });
      try {
        sessionStorage.setItem(
          FORCE_TIMELINE_RANGE_KEY,
          JSON.stringify({
            dateFrom: moment(selectedStartDate).utc().format(),
            dateTo: moment(selectedEndDate).utc().format(),
            createdAt: new Date().toISOString(),
          })
        );
      } catch (_e) { /* sessionStorage may be unavailable - non-critical */ }
      try {
        const LAST_URL_KEY = "app:lastUrl";
        const lastUrl = sessionStorage.getItem(LAST_URL_KEY) || "/bookings";
        let redirectPath = "/bookings";
        if (isRedirectToAdmin) {
          redirectPath = "/admin";
        } else if (keepLastUrlRedirect && !forceMyBookingsRedirect) {
          redirectPath = lastUrl;
        }
        navigation.push(redirectPath);
      } catch (_e) { /* navigation failure is non-critical */ }
    } catch (error) {
      // Original booking is already gone at this point (cancel succeeded);
      // surface a distinct message so admin knows they must create the
      // Information slot manually.
      console.error(
        "BookEquipmentAction: Information slot creation failed after booking cancel during conversion",
        error
      );
      snackbarService.show({
        message:
          "Booking cancelled but creating the Information slot failed. Please create it manually.",
        type: "error",
        duration: DURATION,
      });
    } finally {
      setFetching(false);
    }
  };

  const handleConversionConfirm = async () => {
    setIsConversionConfirmOpen(false);
    setFetching(true);
    const bookingId = editBookingData?.bookingId || editBookingData?.id;
    if (!bookingId) {
      console.error(
        "BookEquipmentAction: conversion aborted - no bookingId on editBookingData"
      );
      snackbarService.show({
        message: "Cannot convert booking - missing booking identifier",
        type: "error",
        duration: DURATION,
      });
      setFetching(false);
      return;
    }
    // Arm the post-cancel step BEFORE firing the cancel mutation: the
    // shared BookingNotificationStatus subscription will pick it up and
    // call it instead of running the default redirect.
    conversionCallbackRef.current = runInformationCreateAfterCancel;
    try {
      await client.mutate({
        mutation: DROP_BOOKING,
        fetchPolicy: "no-cache",
        variables: {
          createdBy: storeUser.user,
          bookingId,
          remark: "Converted to Information",
          ...(hasAllRequiredRoles(storeUser.roles, [BOOKING_ADMIN]) ? { force: true } : {}),
        },
      });
    } catch (error) {
      console.error(
        "BookEquipmentAction: DROP_BOOKING mutation failed during conversion",
        error
      );
      conversionCallbackRef.current = null;
      snackbarService.show({
        message: "Failed to cancel booking during conversion",
        type: "error",
        duration: DURATION,
      });
      setFetching(false);
    }
  };

  const handleConversionCancel = () => {
    setIsConversionConfirmOpen(false);
  };

  const handlerOnAddBooking = async () => {
    // Track click on button
    // trackEvent({ category: "Booking Added", action: "click-event" });
    setPopupEnableStatus(false);
    handleSend();
  };

  const handlerOnEditBooking = async () => {
    setPopupEnableStatus(false);
    handleUpdate();
  };
  const handleOpen = async () => {
    setIsDialogOpen(true);
  };
  const compareValues = () => {
    return compareBookingValues({
      formik,
      pendingReservations,
      bookingEntries: editBookingData?.bookingEntries || [],
      selectedInventoryIds: selectedList || [],
    });
  };

  // Populate handlersRef so PaginationButtons can use these handlers
  useEffect(() => {
    if (handlersRef) {
      handlersRef.current = {
        handlerOnAddBooking,
        handleOpen,
        compareValues
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  });

  return (
    <>
      <>
        <BackdropPortal show={fetching} />
        <BookingNotificationStatus
          onTimeoutCallback={() => {
            // Drop any armed conversion step so a stale subscription event
            // can't fire it after the user dismisses the timeout.
            conversionCallbackRef.current = null;
            setFetching(false);
            timeoutCallbackFactory(
              "Problems might have been occurred with booking, please reload the page to see if the bookings has been created."
            );
          }}
          tryTimes={3}
          timeout={5 * 60 * 1000}
          onBookingCallback={(data) => {
            const subscriptionData = data?.data?.onUpdateBookingCommand;
            // Conversion flow intercept: if a regular -> Information
            // conversion is in flight, run the queued create step instead
            // of the default redirect. Only fires once cancel is fully
            // acknowledged by BE (ALL_SUCCEEDED). Anything else (partial /
            // pending) keeps the ref armed and lets the next subscription
            // tick try again.
            if (
              conversionCallbackRef.current &&
              subscriptionData?.processingResult === "ALL_SUCCEEDED"
            ) {
              const next = conversionCallbackRef.current;
              conversionCallbackRef.current = null;
              setSubscriptionStatus(subscriptionData);
              next();
              return;
            }
            setFetching(false);
            setSubscriptionStatus(subscriptionData);
            const LAST_URL_KEY = "app:lastUrl";
            const lastUrl = sessionStorage.getItem(LAST_URL_KEY) || "/bookings";
            let redirectPath = "/bookings";
            if (isRedirectToAdmin) {
              redirectPath = "/admin";
            } else if (keepLastUrlRedirect && !forceMyBookingsRedirect) {
              redirectPath = lastUrl;
            }
            navigation.push(redirectPath);
          }}
        />
        <div >
          {(isLegendVisible && activeTab === 2) && <BookingScheduleStatus />}
          <BookingActionButtons
            isEditMode={isEditMode}
            isDuplicate={isDuplicate}
            activeTab={activeTab}
            isLegendVisible={isLegendVisible}
            setIsLegendVisible={setIsLegendVisible}
            handlerOnAddBooking={handlerOnAddBooking}
            handleOpen={handleOpen}
            isConflict={conflicts?.length}
            formik={formik}
            disabledSelection={disabledSelection}
            pendingReservations={pendingReservations}
            compareValues={compareValues}
          />
        </div>
      </>
      <ConfirmDialog
        approveText="Save"
        approveColor="primary"
        approveVariant="contained"
        cancelText="Cancel"
        cancelVariant="outlined"
        cancelColor="primary"
        onApprove={() => handlerOnEditBooking()}
        title="Edit booking"
        content={`Please select a reason for editing this booking.`}
        showReasonDD={true}
        reasonValue={reasonValue}
        onReasonChanged={onReasonChanged}
        disableBooking={!reasonValue}
        close={handleOnclose}
        open={isDialogOpen}
        onCancel={handlerOnCancelDeleteBooking}
        fullWidth={false}
        maxWidth="sm"
      />
      <ConfirmDialog
        approveText="Convert"
        approveColor="primary"
        approveVariant="contained"
        cancelText="Cancel"
        cancelVariant="outlined"
        cancelColor="primary"
        onApprove={handleConversionConfirm}
        title="Convert booking to Information"
        content={`This will cancel the existing booking and create a new Information slot in its place. This action cannot be undone. Continue?`}
        disableBooking={false}
        close={handleConversionCancel}
        open={isConversionConfirmOpen}
        onCancel={handleConversionCancel}
        fullWidth={false}
        maxWidth="sm"
      />
    </>
  );
};

export default compose(
  connect(null, {
    loadPendingReservations: loadPendingReservationsAction,
  }),
  withApollo
)(BookEquipmentAction);
